// SME Capture Flow — Phase 4 attribution UI (spec §4 Phase 4).
//
// For each step the expert says who or what triggers it — the one thing the
// system asks directly, because it can't be reliably inferred. The server works
// out, behind the scenes, whether the step is started by a PERSON or happens
// AUTOMATICALLY; that inferred kind is shown back as a small badge. The hidden
// action→event layer is derived separately and not surfaced here.

import React, { useState } from 'react';
import { TimelineEntry } from '../utils/captureTimeline';
import { Attribution } from '../hooks/useCapture';
import '../styles/CaptureAttribution.css';

interface Props {
  timeline: TimelineEntry[];
  attributions: Record<string, Attribution>;
  onSave: (eventId: string, who: string, systems: string[]) => void;
}

const CaptureAttribution: React.FC<Props> = ({ timeline, attributions, onSave }) => {
  // Per-row draft of the "who triggers this" answer, seeded from any saved value.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const valueFor = (id: string) => (id in drafts ? drafts[id] : attributions[id]?.trigger.who ?? '');

  return (
    <ol className="capture-attr-list">
      {timeline.map((e, i) => {
        const attr = attributions[e.id];
        const who = valueFor(e.id);
        return (
          <li key={e.id} className="capture-attr-row">
            <div className="capture-attr-step">
              <span className="capture-attr-num">{i + 1}</span>
              <span className="capture-attr-text">{e.text}</span>
              {attr && (
                <span className={`capture-attr-kind kind-${attr.trigger.kind}`}>
                  {attr.trigger.kind === 'person' ? '👤 person' : '⚙ automatic'}
                </span>
              )}
            </div>
            <div className="capture-attr-input">
              <input
                value={who}
                onChange={(ev) => setDrafts((d) => ({ ...d, [e.id]: ev.target.value }))}
                placeholder="Who kicks this off? e.g. the customer — or 'happens automatically'"
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' && who.trim()) onSave(e.id, who.trim(), []);
                }}
              />
              <button onClick={() => onSave(e.id, who.trim(), [])} disabled={!who.trim()}>
                {attr ? 'Update' : 'Save'}
              </button>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default CaptureAttribution;
